










































































































































































































































































































import { CopyToClipboardButton, OnClickOutside, SvgImage } from '@/app/components';
import { useFeatureFlags, useFilters } from '@/app/composable';
import store from '@/app/store';
import { HarvesterSourceType, StatusCode, stepNameMapping } from '@/modules/data-checkin/constants';
import {
    ClockIcon,
    DocumentAddIcon,
    DuplicateIcon,
    LinkIcon,
    PencilAltIcon,
    RefreshIcon,
    TrashIcon,
    BellIcon,
    MinusIcon,
    DotsVerticalIcon,
    ChevronRightIcon,
    ArrowLeftIcon,
} from '@vue-hero-icons/outline';
import {
    CogIcon,
    BanIcon,
    CalendarIcon,
    CheckCircleIcon,
    StopIcon,
    ExclamationIcon,
    LockClosedIcon,
    UserIcon,
    XCircleIcon,
    InformationCircleIcon,
    ClockIcon as ClockIconSolid,
    DocumentTextIcon,
    ServerIcon,
    DocumentDuplicateIcon,
} from '@vue-hero-icons/solid';
import { computed, defineComponent, PropType, ref } from '@vue/composition-api';
import dayjs from 'dayjs';
import * as R from 'ramda';

export default defineComponent({
    name: 'JobOverview',
    components: {
        UserIcon,
        CalendarIcon,
        TrashIcon,
        PencilAltIcon,
        DocumentAddIcon,
        DuplicateIcon,
        LinkIcon,
        ClockIcon,
        ClockIconSolid,
        RefreshIcon,
        CogIcon,
        CheckCircleIcon,
        StopIcon,
        XCircleIcon,
        ExclamationIcon,
        BanIcon,
        LockClosedIcon,
        SvgImage,
        CopyToClipboardButton,
        BellIcon,
        MinusIcon,
        DotsVerticalIcon,
        OnClickOutside,
        ChevronRightIcon,
        InformationCircleIcon,
        DocumentTextIcon,
        ArrowLeftIcon,
    },
    props: {
        job: {
            type: Object,
            required: true,
        },
        queryParams: {
            type: String,
            default: '{}',
        },
        alert: {
            type: Object as PropType<{ entityId: string | number; alertId: string; enabled: boolean }>,
            required: false,
        },
    },
    setup(props, { emit, root }: { emit: any; root: any }) {
        const { formatDate, formatDateTime } = useFilters();
        const { flag } = useFeatureFlags();
        const isAlertsEnabled = flag('alerts');
        const isOptionsMenuOpen = ref<boolean>(false);

        const canRetry = computed(() => props.job.executionStatus === StatusCode.Cancelled);

        const orderedSteps = computed(() => {
            return R.sort(R.ascend<any>(R.prop('order')), props.job.dataCheckinJobSteps);
        });

        const statusStep: any = computed(() => {
            for (let idx = 0; idx < orderedSteps.value.length; idx += 1) {
                if (
                    orderedSteps.value[idx].status === StatusCode.Configuration ||
                    orderedSteps.value[idx].status === StatusCode.Update
                ) {
                    return orderedSteps.value[idx];
                }
            }

            for (let idx = 0; idx < orderedSteps.value.length; idx += 1) {
                if (orderedSteps.value[idx].status !== StatusCode.Completed) {
                    return orderedSteps.value[idx];
                }
            }
            return orderedSteps.value[orderedSteps.value.length - 1];
        });

        const statusMessage = computed(() => {
            if (canRetry.value) return StatusCode.Cancelled;
            if (props.job.executionStatus === StatusCode.Suspended) return StatusCode.Suspended;
            if (props.job.executionStatus === StatusCode.Configuration)
                return `${props.job.executionStatus}:  ${stepNameMapping[statusStep.value.dataCheckinStepType.name]}`;
            return props.job.executionStatus;
        });

        const statusClass = computed(() => {
            switch (props.job.executionStatus) {
                case StatusCode.Failed:
                    return 'text-white bg-red-700';
                case StatusCode.Cancelled:
                case StatusCode.Suspended:
                    return 'text-white bg-orange-700';
                case StatusCode.Completed:
                    return 'text-white bg-green-600';
                case StatusCode.Queued:
                    return 'text-white bg-primary-400';
                case StatusCode.Running:
                    return 'text-whute bg-blue-700';
                case StatusCode.Idle:
                    return 'text-white bg-teal-700';
                case StatusCode.Deprecated:
                    return 'text-white bg-red-700';
                case StatusCode.Configuration:
                default:
                    return 'text-white bg-neutral-600';
            }
        });

        const harvesterTypeInfo = computed(() => {
            const result: {
                icon: string | null;
                iconClasses: string | null;
                textClasses: string | null;
                tooltip: string | null;
            } = { icon: null, iconClasses: 'text-neutral-800', textClasses: null, tooltip: null };

            switch (props.job.harvesterSource) {
                case HarvesterSourceType.File:
                    result.icon = DocumentTextIcon;
                    result.tooltip = 'File';
                    break;
                case HarvesterSourceType.Api:
                    result.icon = ServerIcon;
                    result.tooltip = 'Data Provider API';
                    break;
                case HarvesterSourceType.InternalApi:
                    result.icon = ServerIcon;
                    result.tooltip = 'Platform API';
                    break;
                case HarvesterSourceType.LargeFiles:
                    result.icon = DocumentDuplicateIcon;
                    result.tooltip = 'Large Files';
                    break;
                default:
                //do nothing
            }
            switch (props.job.fileType) {
                case 'json':
                    if (props.job.harvesterSource !== HarvesterSourceType.InternalApi) {
                        result.iconClasses = 'text-orange-600';
                        result.textClasses = 'text-orange-700 bg-orange-200 border-orange-300 -right-2';
                        result.tooltip += ': JSON';
                    } else {
                        result.iconClasses = 'text-primary-400';
                        result.textClasses = 'text-primary-700 bg-primary-50 border-primary-200 -right-1';
                    }
                    break;
                case 'csv':
                    result.iconClasses = 'text-green-700';
                    result.textClasses = 'text-green-700 bg-green-200 border-green-300 -right-1';
                    result.tooltip += ': CSV';
                    break;
                case 'xml':
                    result.iconClasses = 'text-red-600';
                    result.textClasses = 'text-red-700 bg-red-200 border-red-300 -right-1';
                    result.tooltip += ': XML';
                    break;
                case 'parquet':
                    result.iconClasses = 'text-teal-700';
                    result.textClasses = 'text-teal-700 bg-teal-200 border-teal-300 -right-4';
                    result.tooltip += ': Parquet';
                    break;
                case 'other':
                    result.iconClasses = 'text-blue-600';
                    result.textClasses = 'text-blue-700 bg-blue-200 border-blue-300 -right-1';
                    result.tooltip += ': Other';
                    result.icon = DocumentTextIcon;
                    break;
                default:
                // do nothing;
            }
            return result;
        });

        const user = computed(() => store.state.auth.user);
        const isUserJobCreator = computed(() => user.value.id === Number(props.job?.createdBy?.id));

        const editJob = async (job: any) => {
            if (props.job.executionStatus === StatusCode.Suspended) {
                (root as any).$toastr.w(
                    `This data collection pipeline is suspended as its output asset (dataset) was deleted. You need to edit the Loader step and configure a new output asset in order to be able to use this pipeline again.`,
                    'Warning',
                );
            } else {
                emit('edit', job);
            }
        };

        const appendData = async (job: any) => {
            root.$router.push({ name: 'harvester', params: { id: job.id, queryParams: props.queryParams } });
        };

        const toSteps = async (name: string, jobId: number) => {
            if (name === 'harvester' && props.job.executionStatus === StatusCode.Suspended) {
                (root as any).$toastr.w(
                    `This data collection pipeline is suspended as its output asset (dataset) was deleted. You need to edit the Loader step and configure a new output asset in order to be able to use this pipeline again`,
                    'Warning',
                );
            }
            root.$router.push({ name, params: { id: jobId, queryParams: props.queryParams } });
        };

        const destroyJob = async (job: any) => {
            if (!job.canUse) {
                (root as any).$toastr.e('The Data Collection Pipeline is locked', 'Error');
            } else {
                emit('destroy', job);
            }
        };

        const isFinalized = computed(() => {
            if (props.job.executionStatus === StatusCode.Suspended) return true;
            return props.job.dataCheckinJobSteps.every(
                (step: any) => !!step && step.status !== StatusCode.Configuration && step.status !== StatusCode.Update,
            );
        });

        const accessLevel = computed(() => props.job && props.job?.accessLevel);

        return {
            HarvesterSourceType,
            dayjs,
            formatDate,
            formatDateTime,
            statusMessage,
            statusClass,
            StatusCode,
            orderedSteps,
            statusStep,
            canRetry,
            isUserJobCreator,
            editJob,
            appendData,
            toSteps,
            destroyJob,
            isFinalized,
            accessLevel,
            isAlertsEnabled,
            isOptionsMenuOpen,
            harvesterTypeInfo,
            stepNameMapping,
        };
    },
});
